import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Temporal } from '@js-temporal/polyfill';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { supabase } from '../config/supabase';
import { useAuditUserId, useAuth } from '../auth/AuthContext';
import { useDraftPersistence } from '../hooks/useDraftPersistence';
import {
  matchClassificationOptions,
  matchSiteTypeOptions,
  matchStatusOptions,
  opponentRatingBasisOptions,
  opponentSubjectiveDifficultyOptions,
} from '../data/options';
import { lookupLabel } from '../data/lookups';
import type {
  LookupState,
  MatchEventRecord,
  MatchSiteType,
  MatchStatus,
  OpponentRatingBasis,
  TeamEventRecord,
  TeamEventStatus,
  TeamEventType,
  TimeFormat,
  UserPreferences,
} from '../types';
import { formatTime, toTimeInput } from '../utils/timeFormat';
import { defaultMatchWarmupTime } from '../utils/matchWarmup';
import { addressTimezone, defaultEventTimezone, eventInstant, validTimezone } from '../../shared/eventTimezones';
import { EventTimezoneField } from './EventTimezoneField';
import { isEligibleAdditionalEventTeam } from '../utils/eventTeamEligibility';
import { groupMeReminderOptions, groupMeReminderSummary, normalizeGroupMeReminders } from '../utils/groupmeReminders';
import { BreadcrumbTrail } from './ux/BreadcrumbTrail';
import { ContextBackButton } from './ux/ContextBackButton';
import { DraftRecoveryDialog } from './DraftRecoveryDialog';
import { DraftSavedIndicator } from './DraftSavedIndicator';
import type { BreadcrumbItem, ContextBackDestination } from '../utils/uxNavigation';


type EventEditorMode = 'create' | 'edit';
type EventKind = 'match' | 'team-event';
type EventTypeChoice = TeamEventType;

type TeamOptionRow = {
  id: string;
  workspace_id?: string | null;
  is_active?: boolean | null;
  team_name: string;
  organization_id: string | null;
  season_label: string | null;
  league_configuration_id: string | null;
  home_facility_id: string | null;
  league_name: string | null;
  default_warmup_minutes: number | null;
  groupme_group_id?: string | null;
  default_groupme_event_reminders?: number[] | null;
  groupme_event_reminders_enabled?: boolean | null;
};

type ConversationReminder = { enabled: boolean; amount: number; unit: 'minutes' | 'hours' | 'days' };
const defaultConversationReminders: ConversationReminder[] = [{ enabled: true, amount: 2, unit: 'days' }, { enabled: true, amount: 2, unit: 'hours' }];

type FacilityDetailRow = {
  id: string;
  facility_name: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
};

type UnifiedEventForm = {
  timezone: string;
  timezone_manual_override: boolean;
  event_type: EventTypeChoice;
  team_id: string;
  audience_scope: 'workspace' | 'selected_teams';
  additional_team_ids: string[];
  league_configuration_id: string;
  title: string;
  description: string;
  event_date: string;
  start_time: string;
  end_time: string;
  facility_id: string;
  location_text: string;
  status: TeamEventStatus;
  allow_maybe_rsvp: boolean;
  rsvp_deadline_local: string;
  notes: string;
  match_classification: string;
  match_site_type: MatchSiteType;
  match_status: MatchStatus;
  match_time: string;
  warmup_time: string;
  warmup_use_team_default: boolean;
  opponent_name: string;
  opponent_rating_basis: OpponentRatingBasis;
  opponent_edr_source: 'manual' | 'tennis_record';
  opponent_average_edr: string;
  opponent_edr_overall: string;
  opponent_tennis_record_roster_url: string;
  opponent_subjective_difficulty: string;
  opponent_captain_names: string;
  opponent_captain_email: string;
  opponent_captain_phone: string;
  opponent_co_captain_name: string;
  opponent_co_captain_email: string;
  opponent_co_captain_phone: string;
  court_numbers: string;
  groupme_sync_enabled: boolean;
  groupme_event_title: string;
  groupme_event_description: string;
  groupme_event_image_url: string;
  groupme_location_text: string;
  groupme_location_address: string;
  groupme_event_time_source: 'event_time' | 'match_time' | 'warmup_time';
  groupme_event_end_time: string;
  groupme_reminder_mode: 'team_default' | 'custom';
  groupme_custom_reminders: number[];
};

type ImageLibraryItem = {
  id: string;
  title: string;
  imageUrl: string;
  kind: EventKind;
};

type GroupMeEventActionResult = {
  summary?: {
    failed?: number;
    skipped?: number;
  };
  results?: Array<{ status: string; reason?: string }>;
  error?: string;
};

type SavedEventResult = {
  kind: EventKind;
  id: string;
};

type EventEditorProps = {
  lookups: LookupState;
  refreshLookups: () => Promise<void>;
  userPreferences: UserPreferences;
  timeFormat: TimeFormat;
  mode: EventEditorMode;
  eventId?: string | null;
  readOnly?: boolean;
  writableTeamIds?: string[];
  backLabel?: string;
  onCancel: (result?: { kind?: EventKind; id?: string }) => void;
  onSaved: (result: SavedEventResult) => void;
  onDeleted: (result: SavedEventResult) => void;
};

type Snack = {
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
};

const eventTypeOptions: { value: EventTypeChoice; label: string }[] = [
  { value: 'Match', label: 'Match' },
  { value: 'Practice', label: 'Practice' },
  { value: 'Social', label: 'Social' },
  { value: 'Meeting', label: 'Team Meeting' },
  { value: 'Other', label: 'Other' },
];

const emptyForm: UnifiedEventForm = {
  timezone: defaultEventTimezone,
  timezone_manual_override: false,
  event_type: 'Match',
  team_id: '',
  audience_scope: 'selected_teams',
  additional_team_ids: [],
  league_configuration_id: '',
  title: '',
  description: '',
  event_date: '',
  start_time: '',
  end_time: '',
  facility_id: '',
  location_text: '',
  status: 'Scheduled',
  allow_maybe_rsvp: true,
  rsvp_deadline_local: '',
  notes: '',
  match_classification: 'regular_season',
  match_site_type: 'Unknown',
  match_status: 'Draft',
  match_time: '',
  warmup_time: '',
  warmup_use_team_default: true,
  opponent_name: '',
  opponent_rating_basis: 'avg_edr',
  opponent_edr_source: 'manual',
  opponent_average_edr: '',
  opponent_edr_overall: '',
  opponent_tennis_record_roster_url: '',
  opponent_subjective_difficulty: '3',
  opponent_captain_names: '',
  opponent_captain_email: '',
  opponent_captain_phone: '',
  opponent_co_captain_name: '',
  opponent_co_captain_email: '',
  opponent_co_captain_phone: '',
  court_numbers: '',
  groupme_sync_enabled: true,
  groupme_event_title: '',
  groupme_event_description: '',
  groupme_event_image_url: '',
  groupme_location_text: '',
  groupme_location_address: '',
  groupme_event_time_source: 'match_time',
  groupme_event_end_time: '',
  groupme_reminder_mode: 'team_default',
  groupme_custom_reminders: [],
};

const normalizeGroupMeCompareValue = (value: string | null | undefined): string => String(value || '').trim();

function todayIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

function normalizeNullable(value: string | null | undefined): string | null {
  const next = String(value || '').trim();
  return next === '' ? null : next;
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message;
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string' && error.message) return error.message;
  return fallback;
}

function normalizeEmail(value: string | null | undefined): string {
  return String(value || '').trim().toLowerCase();
}

function isValidEmailAddress(value: string | null | undefined): boolean {
  const email = normalizeEmail(value);
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && email.length <= 254;
}

function addMinutesToTimeInput(time: string | null | undefined, minutes: number): string {
  const normalized = toTimeInput(time || null);
  if (!normalized || !Number.isFinite(minutes)) return '';
  const [hours, mins] = normalized.split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(mins)) return '';
  const total = (((hours * 60 + mins + minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

function deadlineLocalValue(value: string | null | undefined, timezone: string): string {
  if (!value) return '';
  try {
    return Temporal.Instant.from(value).toZonedDateTimeISO(timezone || defaultEventTimezone).toPlainDateTime().toString({ smallestUnit: 'minute' });
  } catch { return ''; }
}

function deadlineInstantValue(value: string, timezone: string): string | null {
  if (!value) return null;
  try {
    return Temporal.PlainDateTime.from(value).toZonedDateTime(timezone || defaultEventTimezone, { disambiguation: 'compatible' }).toInstant().toString();
  } catch { return null; }
}

function eventTypeLabel(value: EventTypeChoice): string {
  return eventTypeOptions.find((option) => option.value === value)?.label || value;
}

function matchLabel(form: UnifiedEventForm): string {
  return form.opponent_name.trim() ? `vs ${form.opponent_name.trim()}` : 'Match';
}

function selectedEventKind(form: UnifiedEventForm): EventKind {
  return form.event_type === 'Match' ? 'match' : 'team-event';
}

function formatFacilityAddress(facility: FacilityDetailRow | null): string {
  if (!facility) return '';
  const cityStateZip = [facility.city, facility.state, facility.zip_code].filter(Boolean).join(' ');
  return [facility.address_line1, facility.address_line2, cityStateZip].filter(Boolean).join(', ');
}

function formatGroupMeFacilityName(facility: FacilityDetailRow | null, fallbackLabel = '', courtNumbers = ''): string {
  const baseName = facility?.facility_name || fallbackLabel;
  if (!baseName) return courtNumbers || '';
  return courtNumbers && !baseName.includes(courtNumbers) ? `${baseName} · Courts ${courtNumbers}` : baseName;
}

export function EventEditor({
  lookups,
  refreshLookups,
  userPreferences,
  timeFormat,
  mode,
  eventId = null,
  readOnly = false,
  writableTeamIds = [],
  backLabel,
  onCancel,
  onSaved,
  onDeleted,
}: EventEditorProps) {
  const auditUserId = useAuditUserId();
  const { isOwner, appUser } = useAuth();
  const canManageWorkspaceEvents = isOwner || appUser?.role === 'admin';
  const [form, setForm] = useState<UnifiedEventForm>({ ...emptyForm, event_date: todayIso() });
  const [baselineForm, setBaselineForm] = useState<UnifiedEventForm>({ ...emptyForm, event_date: todayIso() });
  const [recordKind, setRecordKind] = useState<EventKind>(mode === 'create' ? 'match' : 'team-event');
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [snack, setSnack] = useState<Snack | null>(null);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  const [groupMeEndManuallyEdited, setGroupMeEndManuallyEdited] = useState(false);
  const [groupMeImageUploading, setGroupMeImageUploading] = useState(false);
  const [loadedGroupMeEventId, setLoadedGroupMeEventId] = useState<string | null>(null);
  const [opponentRefreshLoading, setOpponentRefreshLoading] = useState(false);
  const [imageLibraryOpen, setImageLibraryOpen] = useState(false);
  const [imageLibrary, setImageLibrary] = useState<ImageLibraryItem[]>([]);
  const [teamDetails, setTeamDetails] = useState<TeamOptionRow[]>([]);
  const [facilityDetails, setFacilityDetails] = useState<FacilityDetailRow[]>([]);
  const [conversationRemindersEnabled, setConversationRemindersEnabled] = useState(false);
  const [conversationReminders, setConversationReminders] = useState<ConversationReminder[]>(defaultConversationReminders);
  const [reminderAudienceTeamIds, setReminderAudienceTeamIds] = useState<string[]>([]);
  const createInitializedRef = useRef(false);

  const accessibleTeamIds = useMemo(() => new Set(writableTeamIds.length > 0 ? writableTeamIds : lookups.teams.map((team) => team.value)), [lookups.teams, writableTeamIds]);
  const teamDetailsById = useMemo(() => new Map(teamDetails.map((team) => [team.id, team])), [teamDetails]);
  const teams = useMemo<TeamOptionRow[]>(() => lookups.teams
    .filter((team) => accessibleTeamIds.has(team.value))
    .map((team) => {
      const detail = teamDetailsById.get(team.value);
      return {
        id: team.value,
        workspace_id: detail?.workspace_id,
        is_active: detail?.is_active,
        team_name: team.label,
        organization_id: detail?.organization_id || null,
        season_label: detail?.season_label || team.season_label || null,
        league_configuration_id: detail?.league_configuration_id || null,
        home_facility_id: detail?.home_facility_id || null,
        league_name: detail?.league_name || null,
        default_warmup_minutes: detail?.default_warmup_minutes ?? null,
        groupme_group_id: detail?.groupme_group_id || null,
        default_groupme_event_reminders: detail?.default_groupme_event_reminders || null,
      };
  }), [accessibleTeamIds, lookups.teams, teamDetailsById]);
  const teamById = useMemo(() => new Map(teams.map((team) => [team.id, team])), [teams]);
  const selectedTeam = form.team_id ? teamById.get(form.team_id) || null : null;
  const selectedWorkspaceName = selectedTeam?.workspace_id
    ? lookupLabel(lookups, 'workspaces', selectedTeam.workspace_id)
    : '';
  const isMatch = form.event_type === 'Match';
  const facilityDetailsById = useMemo(() => new Map(facilityDetails.map((facility) => [facility.id, facility])), [facilityDetails]);
  const selectedFacility = form.facility_id ? facilityDetailsById.get(form.facility_id) || null : null;
  const selectedFacilityLabel = form.facility_id ? lookupLabel(lookups, 'facilities', form.facility_id) : '';
  const selectedFacilityAddress = formatFacilityAddress(selectedFacility);
  const defaultGroupMeLocationText = formatGroupMeFacilityName(selectedFacility, selectedFacilityLabel, isMatch ? form.court_numbers : '') || (!isMatch ? form.location_text.trim() : '');
  const groupMeLocationTextPreview = form.groupme_location_text.trim() || defaultGroupMeLocationText;
  const groupMeLocationAddressPreview = form.groupme_location_address.trim() || selectedFacilityAddress;
  const groupMeLocationTextHelperText = form.groupme_location_text.trim()
    ? 'Custom GroupMe location text. Clear this field to use the selected facility.'
    : defaultGroupMeLocationText
      ? 'Defaults from the selected facility or CourtCaptain location text.'
      : 'No GroupMe location text is available yet.';
  const groupMeLocationAddressHelperText = form.groupme_location_address.trim()
    ? 'Custom GroupMe address. Clear this field to use the selected facility address.'
    : selectedFacilityAddress
      ? 'Defaults from the selected facility address.'
      : 'No facility address is available to send to GroupMe.';
  const draftPersistence = useDraftPersistence({
    userId: auditUserId,
    screenType: 'unified-event-editor',
    entityId: mode === 'edit' ? eventId || 'unknown-event' : 'new',
    value: form,
    initialValue: baselineForm,
    enabled: !readOnly && !loading,
  });

  const updateForm = (patch: Partial<UnifiedEventForm>) => setForm((current) => ({ ...current, ...patch }));

  useEffect(() => {
    if (form.timezone_manual_override) return;
    const zone = (form.facility_id ? addressTimezone(facilityDetailsById.get(form.facility_id))
      : addressTimezone(form.location_text || form.groupme_location_address)) || defaultEventTimezone;
    if (form.timezone !== zone) setForm((current) => ({ ...current, timezone: zone }));
  }, [facilityDetailsById, form.facility_id, form.location_text, form.groupme_location_address, form.timezone, form.timezone_manual_override]);

  const defaultGroupMeLocationTextFor = useCallback((current: UnifiedEventForm, facilityId = current.facility_id): string => {
    const facility = facilityId ? facilityDetailsById.get(facilityId) || null : null;
    const facilityLabel = facilityId ? lookupLabel(lookups, 'facilities', facilityId) : '';
    return formatGroupMeFacilityName(facility, facilityLabel, current.event_type === 'Match' ? current.court_numbers : '') || (current.event_type === 'Match' ? '' : current.location_text.trim());
  }, [facilityDetailsById, lookups]);

  const defaultGroupMeLocationAddressFor = useCallback((facilityId: string): string => {
    const facility = facilityId ? facilityDetailsById.get(facilityId) || null : null;
    return formatFacilityAddress(facility);
  }, [facilityDetailsById]);

  const applyFacilityChange = useCallback((current: UnifiedEventForm, facilityId: string): UnifiedEventForm => {
    const facility = facilityId ? facilityDetailsById.get(facilityId) || null : null;
    const facilityLabel = facilityId ? lookupLabel(lookups, 'facilities', facilityId) : '';
    const locationText = formatGroupMeFacilityName(facility, facilityLabel, current.event_type === 'Match' ? current.court_numbers : '');
    return {
      ...current,
      facility_id: facilityId,
      // A facility is the canonical source for these values. Keeping explicit
      // copies avoids a stale free-text location in the event and in GroupMe.
      location_text: current.event_type === 'Match' ? current.location_text : locationText,
      groupme_location_text: locationText,
      groupme_location_address: formatFacilityAddress(facility),
    };
  }, [facilityDetailsById, lookups]);

  const applyTeamDefaults = useCallback((teamId: string, current: UnifiedEventForm): UnifiedEventForm => {
    const team = teamById.get(teamId);
    const defaultFacilityId = current.match_site_type === 'Home' ? team?.home_facility_id || '' : current.facility_id;
    const next = applyFacilityChange(current, defaultFacilityId);
    return {
      ...next,
      team_id: teamId,
      league_configuration_id: team?.league_configuration_id || '',
    };
  }, [applyFacilityChange, teamById]);

  const defaultWarmupMinutesForTeam = useCallback((teamId: string): number => teamById.get(teamId)?.default_warmup_minutes ?? 30, [teamById]);
  const defaultWarmupTimeForTeam = useCallback((teamId: string, matchTime: string): string => defaultMatchWarmupTime(matchTime, defaultWarmupMinutesForTeam(teamId)), [defaultWarmupMinutesForTeam]);
  const defaultGroupMeEndTimeForForm = useCallback((current: UnifiedEventForm): string => {
    const base = current.event_type === 'Match' ? current.match_time : current.start_time;
    return addMinutesToTimeInput(base, 120);
  }, []);

  const groupMePublishSignatureFor = useCallback((current: UnifiedEventForm) => {
    const currentIsMatch = current.event_type === 'Match';
    const shared = {
      syncEnabled: current.groupme_sync_enabled,
      eventType: current.event_type,
      teamId: current.team_id,
      eventDate: current.event_date,
      timezone: current.timezone,
      title: normalizeGroupMeCompareValue(current.groupme_event_title),
      description: normalizeGroupMeCompareValue(current.groupme_event_description),
      imageUrl: normalizeGroupMeCompareValue(current.groupme_event_image_url),
      locationText: normalizeGroupMeCompareValue(current.groupme_location_text),
      locationAddress: normalizeGroupMeCompareValue(current.groupme_location_address),
      reminderMode: current.groupme_reminder_mode,
      reminders: normalizeGroupMeReminders(current.groupme_custom_reminders),
    };
    if (currentIsMatch) {
      return {
        ...shared,
        opponentName: normalizeGroupMeCompareValue(current.opponent_name),
        matchSiteType: current.match_site_type,
        matchTime: normalizeGroupMeCompareValue(current.match_time),
        warmupTime: normalizeGroupMeCompareValue(current.warmup_time),
        groupMeTimeSource: current.groupme_event_time_source === 'event_time' ? 'match_time' : current.groupme_event_time_source,
        groupMeEndTime: normalizeGroupMeCompareValue(current.groupme_event_end_time || defaultGroupMeEndTimeForForm(current)),
        facilityId: current.facility_id,
        courtNumbers: normalizeGroupMeCompareValue(current.court_numbers),
        status: current.match_status,
      };
    }
    return {
      ...shared,
      titleFallback: normalizeGroupMeCompareValue(current.title),
      descriptionFallback: normalizeGroupMeCompareValue(current.description),
      startTime: normalizeGroupMeCompareValue(current.start_time),
      endTime: normalizeGroupMeCompareValue(current.end_time),
      facilityId: current.facility_id,
      locationFallback: normalizeGroupMeCompareValue(current.location_text),
      status: current.status,
    };
  }, [defaultGroupMeEndTimeForForm]);

  const hasGroupMePublishChanges = useCallback(() => (
    JSON.stringify(groupMePublishSignatureFor(form)) !== JSON.stringify(groupMePublishSignatureFor(baselineForm))
  ), [baselineForm, form, groupMePublishSignatureFor]);

  const groupMeStartTimeForForm = useCallback((current: UnifiedEventForm): string => {
    if (current.event_type !== 'Match') return current.start_time;
    return current.groupme_event_time_source === 'warmup_time' ? current.warmup_time || current.match_time : current.match_time || current.warmup_time;
  }, []);

  const groupMeEventTimeOptionLabel = (source: UnifiedEventForm['groupme_event_time_source']): string => {
    if (source === 'event_time') return form.start_time ? `${formatTime(form.start_time, timeFormat, form)} (Event)` : 'Event time not set';
    const value = source === 'warmup_time' ? form.warmup_time : form.match_time;
    const label = source === 'warmup_time' ? 'Warmup' : 'Match';
    return value ? `${formatTime(value, timeFormat)} (${label})` : `${label} time not set`;
  };

  const isDefaultGroupMeEndTime = useCallback((current: UnifiedEventForm): boolean => {
    if (!current.groupme_event_end_time) return true;
    const selectedStartTime = groupMeStartTimeForForm(current);
    const defaultFromPrimaryTime = defaultGroupMeEndTimeForForm(current);
    const defaultFromSelectedStartTime = selectedStartTime ? addMinutesToTimeInput(selectedStartTime, 120) : '';
    return current.groupme_event_end_time === defaultFromPrimaryTime || current.groupme_event_end_time === defaultFromSelectedStartTime;
  }, [defaultGroupMeEndTimeForForm, groupMeStartTimeForForm]);

  const buildInitialCreateForm = useCallback(() => {
    const firstTeamId = teams[0]?.id || '';
    const next = applyTeamDefaults(firstTeamId, { ...emptyForm, event_date: todayIso(), team_id: firstTeamId });
    return {
      ...next,
      warmup_time: next.match_time ? defaultWarmupTimeForTeam(firstTeamId, next.match_time) : '',
      groupme_event_end_time: '',
    };
  }, [applyTeamDefaults, defaultWarmupTimeForTeam, teams]);

  useEffect(() => {
    let cancelled = false;
    async function loadTeamDetails() {
      const teamIds = Array.from(accessibleTeamIds);
      if (teamIds.length === 0) {
        setTeamDetails([]);
        return;
      }
      const { data, error } = await supabase
        .from('teams')
        .select('id, workspace_id, is_active, team_name, organization_id, season_label, league_configuration_id, home_facility_id, league_name, default_warmup_minutes, groupme_group_id, default_groupme_event_reminders, groupme_event_reminders_enabled')
        .in('id', teamIds)
        .is('deleted_at', null);
      if (!cancelled && !error) setTeamDetails((data || []) as TeamOptionRow[]);
    }
    void loadTeamDetails();
    return () => {
      cancelled = true;
    };
  }, [accessibleTeamIds]);

  useEffect(() => {
    let cancelled = false;
    async function loadFacilityDetails() {
      const facilityIds = lookups.facilities.map((facility) => facility.value).filter(Boolean);
      if (facilityIds.length === 0) {
        setFacilityDetails([]);
        return;
      }
      const { data, error } = await supabase
        .from('facilities')
        .select('id, facility_name, address_line1, address_line2, city, state, zip_code')
        .in('id', facilityIds)
        .is('deleted_at', null);
      if (!cancelled && !error) setFacilityDetails((data || []) as FacilityDetailRow[]);
    }
    void loadFacilityDetails();
    return () => {
      cancelled = true;
    };
  }, [lookups.facilities]);

  const formFromMatch = useCallback((match: MatchEventRecord): UnifiedEventForm => {
    const matchTime = toTimeInput(match.match_time);
    const warmupTime = toTimeInput(match.warmup_time);
    const source = match.groupme_event_time_source || 'match_time';
    const groupMeEndTime = toTimeInput(match.groupme_event_end_time);
    return {
      ...emptyForm,
      event_type: 'Match',
      timezone: match.timezone || defaultEventTimezone,
      timezone_manual_override: match.timezone_manual_override || false,
      team_id: match.team_id,
      league_configuration_id: match.league_configuration_id,
      event_date: match.match_date,
      start_time: matchTime,
      match_classification: match.match_classification || 'regular_season',
      match_site_type: match.match_site_type || 'Unknown',
      match_status: match.match_status || 'Draft',
      match_time: matchTime,
      warmup_time: warmupTime,
      warmup_use_team_default: match.warmup_use_team_default !== false,
      opponent_name: match.opponent_name || '',
      opponent_rating_basis: match.opponent_rating_basis || 'avg_edr',
      opponent_edr_source: match.opponent_edr_source || 'manual',
      opponent_average_edr: (match.manual_opponent_edr ?? match.opponent_average_edr) == null ? '' : String(match.manual_opponent_edr ?? match.opponent_average_edr),
      opponent_edr_overall: match.opponent_edr_overall == null ? '' : String(match.opponent_edr_overall),
      opponent_tennis_record_roster_url: match.opponent_tennis_record_roster_url || '',
      opponent_subjective_difficulty: match.opponent_subjective_difficulty == null ? '3' : String(match.opponent_subjective_difficulty),
      opponent_captain_names: match.opponent_captain_names || '',
      opponent_captain_email: match.opponent_captain_email || '',
      opponent_captain_phone: match.opponent_captain_phone || '',
      opponent_co_captain_name: match.opponent_co_captain_name || '',
      opponent_co_captain_email: match.opponent_co_captain_email || '',
      opponent_co_captain_phone: match.opponent_co_captain_phone || '',
      facility_id: match.facility_id || '',
      location_text: '',
      court_numbers: match.court_numbers || '',
      notes: match.notes_summary || '',
      groupme_sync_enabled: true,
      groupme_event_title: match.groupme_event_title || '',
      groupme_event_description: match.groupme_event_description || '',
      groupme_event_image_url: match.groupme_event_image_url || '',
      groupme_location_text: match.groupme_location_text || '',
      groupme_location_address: match.groupme_location_address || '',
      groupme_event_time_source: source,
      groupme_event_end_time: groupMeEndTime || addMinutesToTimeInput(matchTime || (source === 'warmup_time' ? warmupTime : matchTime || warmupTime), match.groupme_event_duration_minutes ?? 120),
      groupme_reminder_mode: match.groupme_reminder_mode || 'team_default',
      groupme_custom_reminders: normalizeGroupMeReminders(match.groupme_custom_reminders),
    };
  }, []);

  const formFromTeamEvent = useCallback((event: TeamEventRecord): UnifiedEventForm => ({
    ...emptyForm,
    timezone: event.timezone || defaultEventTimezone,
    timezone_manual_override: event.timezone_manual_override || false,
    event_type: event.event_type === 'Match' ? 'Practice' : event.event_type,
    team_id: event.team_id,
    audience_scope: event.audience_scope === 'workspace' ? 'workspace' : 'selected_teams',
    additional_team_ids: [],
    title: event.title || '',
    description: event.description || '',
    event_date: event.event_date || todayIso(),
    start_time: toTimeInput(event.start_time),
    end_time: toTimeInput(event.end_time),
    facility_id: event.facility_id || '',
    location_text: event.location_text || '',
    status: event.status || 'Scheduled',
    allow_maybe_rsvp: event.allow_maybe_rsvp !== false,
    rsvp_deadline_local: deadlineLocalValue(event.rsvp_deadline_at, event.timezone || defaultEventTimezone),
    notes: event.notes || '',
    groupme_sync_enabled: true,
    groupme_event_title: event.groupme_event_title || '',
    groupme_event_description: event.groupme_event_description || '',
    groupme_event_image_url: event.groupme_event_image_url || '',
    groupme_location_text: event.groupme_location_text || '',
    groupme_location_address: event.groupme_location_address || '',
    groupme_event_time_source: 'event_time',
    groupme_event_end_time: toTimeInput(event.end_time) || addMinutesToTimeInput(event.start_time, 120),
    groupme_reminder_mode: event.groupme_reminder_mode || 'team_default',
    groupme_custom_reminders: normalizeGroupMeReminders(event.groupme_custom_reminders),
  }), []);

  useEffect(() => {
    if (mode !== 'create') return;
    if (createInitializedRef.current && form.team_id) return;
    if (createInitializedRef.current && teams.length === 0) return;
    const next = buildInitialCreateForm();
    setRecordKind('match');
    setLoadedGroupMeEventId(null);
    setForm(next);
    setBaselineForm(next);
    setLoading(false);
    createInitializedRef.current = true;
  }, [buildInitialCreateForm, form.team_id, mode, teams.length]);

  useEffect(() => {
    if (mode !== 'edit') return;
    let cancelled = false;
    async function loadRecord() {
      if (!eventId) return;
      setLoading(true);
      const { data: match, error: matchError } = await supabase.from('match_events').select('*').eq('id', eventId).maybeSingle();
      if (!cancelled && match && !matchError) {
        const next = formFromMatch(match as MatchEventRecord);
        setRecordKind('match');
        setLoadedGroupMeEventId((match as MatchEventRecord).groupme_event_id || null);
        setGroupMeEndManuallyEdited(Boolean((match as MatchEventRecord).groupme_event_end_time) && !isDefaultGroupMeEndTime(next));
        setForm(next);
        setBaselineForm(next);
        setLoading(false);
        return;
      }

      const { data: event, error } = await supabase.from('events').select('*').eq('id', eventId).maybeSingle();
      if (!cancelled) {
        if (event && !error) {
          const { data: associations, error: associationsError } = await supabase
            .from('event_teams')
            .select('team_id,is_primary')
            .eq('event_id', eventId);
          if (cancelled) return;
          if (associationsError) {
            setSnack({ severity: 'error', message: `Unable to load event teams. ${associationsError.message}` });
            setLoading(false);
            return;
          }
          const additionalTeamIds = (associations || [])
            .filter((row) => !row.is_primary)
            .map((row) => row.team_id);
          const next = { ...formFromTeamEvent(event as TeamEventRecord), additional_team_ids: additionalTeamIds };
          setRecordKind('team-event');
          setLoadedGroupMeEventId((event as TeamEventRecord).groupme_event_id || null);
          setGroupMeEndManuallyEdited(Boolean((event as TeamEventRecord).end_time));
          setForm(next);
          setBaselineForm(next);
        } else {
          setSnack({ severity: 'error', message: error?.message || matchError?.message || 'Unable to load event.' });
        }
        setLoading(false);
      }
    }

    void loadRecord();
    return () => {
      cancelled = true;
    };
  }, [eventId, formFromMatch, formFromTeamEvent, isDefaultGroupMeEndTime, mode]);

  useEffect(() => {
    const associatedTeams = form.audience_scope === 'workspace' ? [] : [form.team_id, ...form.additional_team_ids].filter(Boolean);
    setReminderAudienceTeamIds((current) => current.length ? current.filter((teamId) => associatedTeams.includes(teamId)) : associatedTeams);
  }, [form.additional_team_ids, form.audience_scope, form.team_id]);

  useEffect(() => {
    if (mode === 'create') setConversationRemindersEnabled(Boolean(selectedTeam?.groupme_event_reminders_enabled));
  }, [mode, selectedTeam?.groupme_event_reminders_enabled]);

  useEffect(() => {
    if (mode !== 'edit' || !eventId) return;
    let cancelled = false;
    void (async () => {
      const session = (await supabase.auth.getSession()).data.session;
      if (!session) return;
      const kind = recordKind === 'match' ? 'match' : 'event';
      const response = await fetch(`/api/events/reminders?kind=${kind}&eventId=${encodeURIComponent(eventId)}`, { headers: { Authorization: `Bearer ${session.access_token}` } });
      const payload = await response.json().catch(() => ({})) as { reminders?: Array<{ enabled?: boolean; reminder_number?: number; amount?: number; unit?: ConversationReminder['unit']; groupme_event_reminder_audiences?: Array<{ team_id: string }> }> };
      if (cancelled || !response.ok || !Array.isArray(payload.reminders) || payload.reminders.length === 0) return;
      const savedReminders = payload.reminders;
      setConversationRemindersEnabled(savedReminders.some((row) => row.enabled));
      setConversationReminders([0, 1].map((index) => {
        const row = savedReminders.find((item) => Number(item.reminder_number) === index + 1);
        return row ? { enabled: row.enabled !== false, amount: Number(row.amount) || defaultConversationReminders[index].amount, unit: row.unit || defaultConversationReminders[index].unit } : defaultConversationReminders[index];
      }));
      setReminderAudienceTeamIds([...new Set(savedReminders.flatMap((row) => (row.groupme_event_reminder_audiences || []).map((audience) => audience.team_id)))]);
    })();
    return () => { cancelled = true; };
  }, [eventId, mode, recordKind]);

  useEffect(() => {
    let cancelled = false;
    async function loadImages() {
      const teamIds = Array.from(accessibleTeamIds);
      if (teamIds.length === 0) return;
      const [matches, events] = await Promise.all([
        supabase.from('match_events').select('id, opponent_name, groupme_event_image_url').in('team_id', teamIds).not('groupme_event_image_url', 'is', null).limit(25),
        supabase.from('events').select('id, title, groupme_event_image_url').in('team_id', teamIds).not('groupme_event_image_url', 'is', null).limit(25),
      ]);
      if (cancelled) return;
      const items: ImageLibraryItem[] = [
        ...((matches.data || []) as Array<{ id: string; opponent_name: string | null; groupme_event_image_url: string | null }>).map((item) => ({
          id: item.id,
          title: item.opponent_name || 'Match image',
          imageUrl: item.groupme_event_image_url || '',
          kind: 'match' as const,
        })),
        ...((events.data || []) as Array<{ id: string; title: string | null; groupme_event_image_url: string | null }>).map((item) => ({
          id: item.id,
          title: item.title || 'Event image',
          imageUrl: item.groupme_event_image_url || '',
          kind: 'team-event' as const,
        })),
      ].filter((item) => item.imageUrl);
      setImageLibrary(items);
    }
    void loadImages();
    return () => {
      cancelled = true;
    };
  }, [accessibleTeamIds]);

  const requestNavigation = (navigate: () => void) => {
    if (draftPersistence.isDirty) {
      setPendingNavigation(() => navigate);
      setDiscardOpen(true);
      return;
    }
    navigate();
  };

  const goBack = () => onCancel({ kind: recordKind, id: eventId || undefined });
  const cancelEdit = () => requestNavigation(goBack);

  const onBreadcrumbNavigate = (item: BreadcrumbItem) => {
    if (item.label === 'Team Events') {
      requestNavigation(() => onCancel());
      return;
    }
    if (item.label !== 'Edit' && mode === 'edit') {
      requestNavigation(goBack);
    }
  };

  const confirmDiscard = () => {
    draftPersistence.discardDraft();
    setDiscardOpen(false);
    const navigate = pendingNavigation;
    setPendingNavigation(null);
    navigate?.();
  };

  const confirmSaveAndNavigate = async () => {
    const navigate = pendingNavigation;
    const saved = await save(navigate || undefined);
    if (!saved) return;
    setDiscardOpen(false);
    setPendingNavigation(null);
  };

  const changeTeam = (teamId: string) => {
    setForm((current) => {
      const next = applyTeamDefaults(teamId, current);
      const matchTime = next.match_time || next.start_time;
      const warmup = current.warmup_use_team_default ? defaultWarmupTimeForTeam(teamId, matchTime) : next.warmup_time;
      const withWarmup = { ...next, warmup_time: warmup };
      return {
        ...withWarmup,
        groupme_event_end_time: groupMeEndManuallyEdited ? current.groupme_event_end_time : defaultGroupMeEndTimeForForm(withWarmup),
      };
    });
  };

  const changeEventType = (_event: unknown, nextType: EventTypeChoice | null) => {
    if (!nextType) return;
    setRecordKind(selectedEventKind({ ...form, event_type: nextType }));
    setForm((current) => {
      const next = { ...current, event_type: nextType };
      return {
        ...next,
        groupme_event_time_source: nextType === 'Match' ? 'match_time' : 'event_time',
        groupme_event_end_time: groupMeEndManuallyEdited ? current.groupme_event_end_time : defaultGroupMeEndTimeForForm(next),
      };
    });
  };

  const changeMatchTime = (matchTime: string) => {
    setForm((current) => {
      const next = {
        ...current,
        start_time: matchTime,
        match_time: matchTime,
        warmup_time: current.warmup_use_team_default
          ? defaultWarmupTimeForTeam(current.team_id, matchTime) : current.warmup_time,
      };
      return {
        ...next,
        groupme_event_end_time: !groupMeEndManuallyEdited || isDefaultGroupMeEndTime(current) ? defaultGroupMeEndTimeForForm(next) : current.groupme_event_end_time,
      };
    });
  };

  const changeEventStartTime = (startTime: string) => {
    setForm((current) => {
      const next = { ...current, start_time: startTime };
      return {
        ...next,
        groupme_event_end_time: !groupMeEndManuallyEdited || isDefaultGroupMeEndTime(current) ? defaultGroupMeEndTimeForForm(next) : current.groupme_event_end_time,
      };
    });
  };

  const useTeamDefaultWarmup = (checked: boolean) => {
    setForm((current) => {
      const next = { ...current, warmup_use_team_default: checked, warmup_time: checked ? defaultWarmupTimeForTeam(current.team_id, current.match_time) : current.warmup_time };
      return {
        ...next,
        groupme_event_end_time: groupMeEndManuallyEdited ? current.groupme_event_end_time : defaultGroupMeEndTimeForForm(next),
      };
    });
  };

  const updateGroupMeReminder = (index: number, nextValue: string) => {
    setForm((current) => {
      const reminders = normalizeGroupMeReminders(current.groupme_custom_reminders);
      const next = [...reminders];
      if (nextValue === '') next.splice(index, 1);
      else next[index] = Number(nextValue);
      return { ...current, groupme_custom_reminders: normalizeGroupMeReminders(next) };
    });
  };

  const uploadGroupMeImage = async (file: File | null) => {
    if (!file) return;
    if (!form.team_id) {
      setSnack({ severity: 'warning', message: 'Select a team before uploading a GroupMe image.' });
      return;
    }
    setGroupMeImageUploading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error('Sign in again before uploading a GroupMe image.');
      const body = new FormData();
      body.append('teamId', form.team_id);
      body.append('image', file);
      const response = await fetch('/api/groupme/upload-image', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body,
      });
      const result = await response.json() as { imageUrl?: string; error?: string };
      if (!response.ok || !result.imageUrl) throw new Error(result.error || 'Unable to upload image to GroupMe.');
      updateForm({ groupme_event_image_url: result.imageUrl });
      setSnack({ severity: 'success', message: 'Image uploaded to GroupMe.' });
    } catch (error) {
      setSnack({ severity: 'error', message: errorMessage(error, 'Unable to upload image to GroupMe.') });
    } finally {
      setGroupMeImageUploading(false);
    }
  };

  const refreshOpponentTennisRecord = async () => {
    if (mode !== 'edit' || !eventId) {
      setSnack({ severity: 'error', message: 'Save the match before refreshing TennisRecord opponent data.' });
      return;
    }
    if (!form.opponent_tennis_record_roster_url.trim()) {
      setSnack({ severity: 'error', message: 'Add an opponent TennisRecord roster URL before refreshing.' });
      return;
    }
    setOpponentRefreshLoading(true);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      const response = await fetch('/api/tennisrecord/opponent-refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ matchEventId: eventId, url: form.opponent_tennis_record_roster_url }),
      });
      const result = await response.json() as { parsed?: { overall?: number | null }; error?: string };
      if (!response.ok) throw new Error(result.error || 'Unable to refresh opponent TennisRecord roster.');
      setForm((current) => ({
        ...current,
        opponent_edr_overall: result.parsed?.overall == null ? current.opponent_edr_overall : String(result.parsed.overall),
      }));
      setSnack({ severity: 'success', message: 'Opponent TennisRecord EDR refreshed.' });
    } catch (error) {
      setSnack({ severity: 'error', message: errorMessage(error, 'Unable to refresh opponent TennisRecord roster.') });
    } finally {
      setOpponentRefreshLoading(false);
    }
  };

  const validate = (): string | null => {
    if (!validTimezone(form.timezone)) return 'Choose a valid IANA timezone.';
    if (readOnly) return 'You have view-only access.';
    if (!form.team_id) return 'Team is required.';
    if (!form.event_date) return isMatch ? 'Match Date is required.' : 'Date is required.';
    if (!isMatch && form.audience_scope === 'workspace' && !canManageWorkspaceEvents) return 'Workspace administrator access is required for an Entire Workspace event.';
    if (!isMatch && form.audience_scope === 'selected_teams' && form.additional_team_ids.some((id) => !isEligibleAdditionalEventTeam(teamById.get(id), selectedTeam))) {
      return 'Remove unavailable additional teams. Additional teams must be active and in the primary team\'s workspace.';
    }
    if (isMatch) {
      if (!form.match_classification) return 'Match Classification is required.';
      if (!form.match_status) return 'Match Status is required.';
      if (form.opponent_average_edr !== '' && (!Number.isFinite(Number(form.opponent_average_edr)) || Number(form.opponent_average_edr) < 1 || Number(form.opponent_average_edr) > 7)) return 'Manual Opponent EDR must be between 1.00 and 7.00.';
      if (!Number.isFinite(Number(form.opponent_subjective_difficulty)) || Number(form.opponent_subjective_difficulty) < 1 || Number(form.opponent_subjective_difficulty) > 5) return 'Opponent difficulty must be between 1 and 5.';
      if (form.opponent_tennis_record_roster_url && !/^https?:\/\/(www\.)?tennisrecord\.com\/adult\/teamprofile\.aspx/i.test(form.opponent_tennis_record_roster_url.trim())) return 'Opponent TennisRecord Team URL must be an adult team profile page.';
    } else if (!form.title.trim()) {
      return 'Title is required.';
    }
    if (!isMatch && form.rsvp_deadline_local) {
      if (!form.start_time) return 'Set an event start time before adding an RSVP deadline.';
      const deadline = deadlineInstantValue(form.rsvp_deadline_local, form.timezone);
      const start = eventInstant(form.event_date, form.start_time, form.timezone)?.toString();
      if (!deadline || !start) return 'Enter a valid RSVP deadline.';
      if (deadline >= start) return 'RSVP deadline must be before the event start time.';
    }
    if (form.opponent_captain_email && !isValidEmailAddress(form.opponent_captain_email)) return 'Opposing Captain Email must be a valid email address.';
    if (form.opponent_co_captain_email && !isValidEmailAddress(form.opponent_co_captain_email)) return 'Opposing Co-Captain Email must be a valid email address.';
    if (form.groupme_event_time_source === 'warmup_time' && !form.warmup_time) return 'Warmup Time is required when GroupMe event time uses warmup time.';
    if (form.groupme_event_end_time && !toTimeInput(form.groupme_event_end_time)) return 'GroupMe End Time must be a valid time.';
    return null;
  };

  const pushSavedGroupMeEvent = async (savedId: string, kind: 'match' | 'event') => {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData.session?.access_token;
    if (!token) throw new Error('Sign in again before updating the linked GroupMe event.');
    const response = await fetch('/api/groupme/events', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        action: 'update',
        items: [{ kind, id: savedId }],
      }),
    });
    const result = await response.json() as GroupMeEventActionResult;
    const failed = result.summary?.failed || 0;
    const skipped = result.summary?.skipped || 0;
    if (!response.ok || failed > 0 || skipped > 0) {
      throw new Error(result.error || result.results?.find((item) => item.status !== 'updated')?.reason || 'Unable to update the linked GroupMe event.');
    }
  };

  const save = async (afterSave?: () => void): Promise<boolean> => {
    const validationError = validate();
    if (validationError) {
      setSnack({ severity: 'error', message: validationError });
      return false;
    }
    setSaving(true);
    try {
      if (isMatch) {
        const manualOpponentEdr = form.opponent_average_edr === '' ? null : Number(form.opponent_average_edr);
        const tennisRecordOpponentEdr = form.opponent_edr_overall === '' ? null : Number(Number(form.opponent_edr_overall).toFixed(2));
        const payload = {
          timezone: form.timezone,
          timezone_manual_override: form.timezone_manual_override,
          team_id: form.team_id,
          league_configuration_id: form.league_configuration_id,
          match_classification: form.match_classification,
          match_site_type: form.match_site_type || 'Unknown',
          match_date: form.event_date,
          match_time: normalizeNullable(form.match_time),
          warmup_time: normalizeNullable(form.warmup_time),
          warmup_use_team_default: form.warmup_use_team_default,
          opponent_name: form.opponent_name.trim() || 'TBD',
          opponent_average_edr: form.opponent_edr_source === 'tennis_record' ? tennisRecordOpponentEdr : manualOpponentEdr,
          manual_opponent_edr: manualOpponentEdr,
          opponent_edr_source: form.opponent_edr_source,
          opponent_rating_basis: form.opponent_rating_basis,
          opponent_subjective_difficulty: Number(form.opponent_subjective_difficulty),
          opponent_tennis_record_roster_url: normalizeNullable(form.opponent_tennis_record_roster_url),
          opponent_captain_names: normalizeNullable(form.opponent_captain_names),
          opponent_captain_email: normalizeEmail(form.opponent_captain_email) || null,
          opponent_captain_phone: normalizeNullable(form.opponent_captain_phone),
          opponent_co_captain_name: normalizeNullable(form.opponent_co_captain_name),
          opponent_co_captain_email: normalizeEmail(form.opponent_co_captain_email) || null,
          opponent_co_captain_phone: normalizeNullable(form.opponent_co_captain_phone),
          facility_id: form.facility_id || null,
          court_numbers: normalizeNullable(form.court_numbers),
          groupme_event_title: form.groupme_sync_enabled ? normalizeNullable(form.groupme_event_title) : null,
          groupme_event_description: form.groupme_sync_enabled ? normalizeNullable(form.groupme_event_description) : null,
          groupme_event_image_url: form.groupme_sync_enabled ? normalizeNullable(form.groupme_event_image_url) : null,
          groupme_event_time_source: form.groupme_event_time_source === 'event_time' ? 'match_time' : form.groupme_event_time_source,
          groupme_event_end_time: form.groupme_sync_enabled ? normalizeNullable(form.groupme_event_end_time || defaultGroupMeEndTimeForForm(form)) : null,
          groupme_location_text: form.groupme_sync_enabled ? normalizeNullable(form.groupme_location_text) : null,
          groupme_location_address: form.groupme_sync_enabled ? normalizeNullable(form.groupme_location_address) : null,
          groupme_reminder_mode: form.groupme_reminder_mode,
          groupme_custom_reminders: form.groupme_reminder_mode === 'custom' ? normalizeGroupMeReminders(form.groupme_custom_reminders) : null,
          match_status: form.match_status,
          ...(mode === 'edit' && eventId ? { updated_by: auditUserId || null } : { lineup_status: 'Not Started', created_by: auditUserId || null, updated_by: auditUserId || null }),
          notes_summary: normalizeNullable(form.notes),
        };
        const operation = mode === 'edit' && eventId
          ? supabase.from('match_events').update(payload).eq('id', eventId)
          : supabase.from('match_events').insert(payload).select('id').single();
        const { data, error } = await operation;
        if (error) throw error;
        const savedId = mode === 'edit' && eventId ? eventId : String((data as { id: string }).id);
        if (mode === 'create') {
          const { error: availabilityError } = await supabase.rpc('initialize_availability_for_match', { p_match_event_id: savedId });
          if (availabilityError) throw availabilityError;
        }
        const reminderSession = (await supabase.auth.getSession()).data.session;
        if (!reminderSession) throw new Error('Sign in again before saving GroupMe reminders.');
        const reminderResponse = await fetch('/api/events/reminders', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${reminderSession.access_token}` },
          body: JSON.stringify({
            kind: 'match', eventId: savedId,
            remindersEnabled: conversationRemindersEnabled || (mode === 'create' && Boolean(selectedTeam?.groupme_event_reminders_enabled)),
            reminders: conversationReminders, audienceTeamIds: reminderAudienceTeamIds.length ? reminderAudienceTeamIds : [form.team_id],
          }),
        });
        const reminderResult = await reminderResponse.json().catch(() => ({})) as { error?: string };
        if (!reminderResponse.ok) throw new Error(reminderResult.error || 'Unable to save GroupMe reminders.');
        draftPersistence.clearDraft();
        if (mode === 'edit' && loadedGroupMeEventId && hasGroupMePublishChanges()) {
          await pushSavedGroupMeEvent(savedId, 'match');
        }
        await refreshLookups();
        const result: SavedEventResult = { kind: 'match', id: savedId };
        if (afterSave) afterSave();
        else onSaved(result);
        return true;
      }

      const payload = {
        timezone: form.timezone,
        timezone_manual_override: form.timezone_manual_override,
        team_id: form.team_id,
        workspace_id: selectedTeam?.workspace_id || null,
        audience_scope: form.audience_scope,
        event_type: form.event_type as Exclude<TeamEventType, 'Match'>,
        title: form.title.trim(),
        description: normalizeNullable(form.description),
        event_date: form.event_date,
        start_time: form.start_time || null,
        end_time: form.end_time || null,
        facility_id: form.facility_id || null,
        location_text: normalizeNullable(form.location_text),
        status: form.status,
        allow_maybe_rsvp: form.allow_maybe_rsvp,
        rsvp_deadline_at: deadlineInstantValue(form.rsvp_deadline_local, form.timezone),
        groupme_event_title: form.audience_scope === 'selected_teams' && form.groupme_sync_enabled ? normalizeNullable(form.groupme_event_title) : null,
        groupme_event_description: form.audience_scope === 'selected_teams' && form.groupme_sync_enabled ? normalizeNullable(form.groupme_event_description) : null,
        groupme_event_image_url: form.audience_scope === 'selected_teams' && form.groupme_sync_enabled ? normalizeNullable(form.groupme_event_image_url) : null,
        groupme_location_text: form.audience_scope === 'selected_teams' && form.groupme_sync_enabled ? normalizeNullable(form.groupme_location_text) : null,
        groupme_location_address: form.audience_scope === 'selected_teams' && form.groupme_sync_enabled ? normalizeNullable(form.groupme_location_address) : null,
        groupme_reminder_mode: form.groupme_reminder_mode,
        groupme_custom_reminders: form.groupme_reminder_mode === 'custom' ? normalizeGroupMeReminders(form.groupme_custom_reminders) : null,
        visibility: 'Team',
        notes: normalizeNullable(form.notes),
        updated_by: auditUserId,
        ...(mode === 'create' ? { created_by: auditUserId } : {}),
      };
      const operation = mode === 'edit' && eventId
        ? supabase.from('events').update(payload).eq('id', eventId)
        : supabase.from('events').insert(payload).select('id').single();
      const { data, error } = await operation;
      if (error) throw error;
      const savedId = mode === 'edit' && eventId ? eventId : String((data as { id: string }).id);
      const associationSession = (await supabase.auth.getSession()).data.session;
      if (!associationSession) throw new Error('Sign in again before saving event teams.');
      const associationResponse = await fetch('/api/events/teams', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${associationSession.access_token}` },
        body: JSON.stringify({ eventId: savedId, audienceScope: form.audience_scope, additionalTeamIds: form.audience_scope === 'workspace' ? [] : form.additional_team_ids }),
      });
      const associationResult = await associationResponse.json().catch(() => ({})) as { error?: string };
      if (!associationResponse.ok) throw new Error(associationResult.error || 'Unable to save event teams.');
      const reminderSession = (await supabase.auth.getSession()).data.session;
      if (!reminderSession) throw new Error('Sign in again before saving GroupMe reminders.');
      const reminderResponse = await fetch('/api/events/reminders', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${reminderSession.access_token}` },
        body: JSON.stringify({
          kind: 'event', eventId: savedId,
          remindersEnabled: form.audience_scope === 'selected_teams' && (conversationRemindersEnabled || (mode === 'create' && Boolean(selectedTeam?.groupme_event_reminders_enabled))),
          reminders: conversationReminders,
          audienceTeamIds: form.audience_scope === 'workspace' ? [] : reminderAudienceTeamIds.length ? reminderAudienceTeamIds : [form.team_id, ...form.additional_team_ids].filter(Boolean),
        }),
      });
      const reminderResult = await reminderResponse.json().catch(() => ({})) as { error?: string };
      if (!reminderResponse.ok) throw new Error(reminderResult.error || 'Unable to save GroupMe reminders.');
      draftPersistence.clearDraft();
      if (mode === 'edit' && loadedGroupMeEventId && hasGroupMePublishChanges()) {
        await pushSavedGroupMeEvent(savedId, 'event');
      }
      await refreshLookups();
      const result: SavedEventResult = { kind: 'team-event', id: savedId };
      if (afterSave) afterSave();
      else onSaved(result);
      return true;
    } catch (error) {
      setSnack({ severity: 'error', message: errorMessage(error, 'Unable to save event.') });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const unpublishBeforeDelete = async (): Promise<boolean> => {
    if (!loadedGroupMeEventId || !eventId) return true;
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData.session?.access_token;
    if (!token) throw new Error('Sign in again before deleting this linked GroupMe event.');
    const response = await fetch('/api/groupme/events', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        action: 'unpublish',
        items: [{ kind: recordKind === 'match' ? 'match' : 'event', id: eventId }],
        reason: `Unpublished because the CourtCaptain ${recordKind === 'match' ? 'match' : 'team event'} was deleted.`,
      }),
    });
    const result = await response.json() as { summary?: { failed?: number; skipped?: number }; results?: Array<{ status: string; reason?: string }>; error?: string };
    if (!response.ok || (result.summary?.failed || 0) > 0 || (result.summary?.skipped || 0) > 0) {
      throw new Error(result.error || result.results?.find((item) => item.status !== 'unpublished')?.reason || 'Unable to unpublish the linked GroupMe event.');
    }
    return true;
  };

  const deleteCurrentEvent = async () => {
    if (readOnly || mode !== 'edit' || !eventId) return;
    const label = isMatch ? 'match' : 'team event';
    const linkedWarning = loadedGroupMeEventId
      ? ' This will also unpublish the linked GroupMe event before deleting it in CourtCaptain. Republishing later will create a new GroupMe event.'
      : '';
    if (!window.confirm(`Delete this ${label}?${linkedWarning}`)) return;
    setDeleting(true);
    try {
      await unpublishBeforeDelete();
      const table = recordKind === 'match' ? 'match_events' : 'events';
      const { error } = await supabase
        .from(table)
        .update({
          deleted_at: new Date().toISOString(),
          deleted_by: auditUserId || null,
          delete_reason: 'Deleted from Unified Event Editor',
          updated_by: auditUserId || null,
        })
        .eq('id', eventId);
      if (error) throw error;
      draftPersistence.clearDraft();
      await refreshLookups();
      onDeleted({ kind: recordKind, id: eventId });
    } catch (error) {
      setSnack({ severity: 'error', message: errorMessage(error, `Unable to delete this ${label}.`) });
    } finally {
      setDeleting(false);
    }
  };

  const displayLabel = mode === 'create'
    ? 'Add Event'
    : isMatch
      ? matchLabel(form)
      : form.title.trim() || eventTypeLabel(form.event_type);
  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Team Events', path: '/team-events' },
    ...(mode === 'edit' ? [{ label: displayLabel, path: recordKind === 'match' && eventId ? `/matches/${eventId}` : eventId ? `/team-events/${eventId}` : undefined }] : []),
    { label: mode === 'create' ? 'Add Event' : 'Edit', current: true },
  ];
  const backDestination: ContextBackDestination = {
    label: backLabel || (mode === 'create' ? 'Team Events' : recordKind === 'match' ? 'Match' : eventTypeLabel(form.event_type).replace('Team ', '')),
    page: recordKind === 'match' ? 'match-detail' : 'team-events',
    path: recordKind === 'match' && eventId ? `/matches/${eventId}` : eventId ? `/team-events/${eventId}` : '/team-events',
    source: 'canonical-parent',
  };
  const saveLabel = mode === 'create' ? 'Create Event' : 'Save Changes';
  const teamContext = selectedTeam
    ? [
      lookupLabel(lookups, 'organizations', selectedTeam.organization_id || ''),
      selectedTeam.season_label,
      isMatch ? lookupLabel(lookups, 'league_configurations', selectedTeam.league_configuration_id || '') : null,
    ].filter(Boolean).join(' / ')
    : '';
  const showGroupMeSection = !isMatch && form.audience_scope === 'workspace'
    ? false
    : form.groupme_sync_enabled || Boolean(selectedTeam?.groupme_group_id) || Boolean(form.groupme_event_title || form.groupme_event_description || form.groupme_event_image_url);

  if (loading) {
    return (
      <Stack spacing={2} alignItems="flex-start">
        <BreadcrumbTrail items={breadcrumbs} onNavigate={onBreadcrumbNavigate} />
        <CircularProgress size={28} />
      </Stack>
    );
  }

  return (
    <Box sx={{ pb: 10 }}>
      <Stack spacing={1.5} sx={{ mb: 2 }}>
        <BreadcrumbTrail items={breadcrumbs} onNavigate={onBreadcrumbNavigate} />
        <ContextBackButton destination={backDestination} onBack={() => cancelEdit()} />
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            {mode === 'create' ? 'Create Event' : isMatch ? 'Edit Match' : 'Edit Team Event'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Save changes when the event setup is ready.
          </Typography>
        </Box>
      </Stack>

      <Stack spacing={2}>
        {mode === 'create' && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Event Type</Typography>
            <ToggleButtonGroup exclusive value={form.event_type} onChange={changeEventType} aria-label="Event Type">
              {eventTypeOptions.map((option) => (
                <ToggleButton key={option.value} value={option.value}>{option.label}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Paper>
        )}

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>{isMatch ? 'Match' : eventTypeLabel(form.event_type)}</Typography>
          <Stack spacing={1.5}>
            {!isMatch && (
              <FormControl fullWidth required>
                <InputLabel>Event Audience</InputLabel>
                <Select
                  label="Event Audience"
                  value={form.audience_scope}
                  onChange={(event) => {
                    const audienceScope = event.target.value as UnifiedEventForm['audience_scope'];
                    updateForm({
                      audience_scope: audienceScope,
                      additional_team_ids: audienceScope === 'workspace' ? [] : form.additional_team_ids,
                      groupme_sync_enabled: audienceScope === 'workspace' ? false : form.groupme_sync_enabled,
                    });
                  }}
                >
                  <MenuItem value="selected_teams">Selected Teams</MenuItem>
                  <MenuItem value="workspace" disabled={!canManageWorkspaceEvents}>Entire Workspace</MenuItem>
                </Select>
              </FormControl>
            )}
            {(isMatch || form.audience_scope === 'selected_teams') ? (
              <FormControl fullWidth required>
                <InputLabel>{isMatch ? 'Team' : 'Primary Team'}</InputLabel>
                <Select label={isMatch ? 'Team' : 'Primary Team'} value={form.team_id} onChange={(event) => changeTeam(event.target.value)}>
                  {teams.map((team) => <MenuItem key={team.id} value={team.id}>{team.team_name}</MenuItem>)}
                </Select>
              </FormControl>
            ) : (
              <Alert severity="info">
                Visible to all eligible players in {selectedWorkspaceName || 'this workspace'}. Newly eligible players will automatically gain access.
              </Alert>
            )}
            {teamContext && <Typography variant="body2" color="text.secondary">{teamContext}</Typography>}
            {isMatch ? (
              <>
                <TextField label="Opponent Name" value={form.opponent_name} onChange={(event) => updateForm({ opponent_name: event.target.value })} fullWidth required />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                  <FormControl fullWidth required>
                    <InputLabel>Match Status</InputLabel>
                    <Select label="Match Status" value={form.match_status} onChange={(event) => updateForm({ match_status: event.target.value as MatchStatus })}>
                      {matchStatusOptions.map((option) => <MenuItem key={option.value} value={option.value} disabled={option.value === 'Scores Entered' || option.value === 'Completed'}>{option.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth required>
                    <InputLabel>Match Classification</InputLabel>
                    <Select label="Match Classification" value={form.match_classification} onChange={(event) => updateForm({ match_classification: event.target.value })}>
                      {matchClassificationOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth required>
                    <InputLabel>Opponent Ranking Basis</InputLabel>
                    <Select label="Opponent Ranking Basis" value={form.opponent_rating_basis} onChange={(event) => updateForm({ opponent_rating_basis: event.target.value as OpponentRatingBasis })}>
                      {opponentRatingBasisOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Box>
                {form.opponent_rating_basis === 'subjective' ? (
                  <FormControl fullWidth>
                    <InputLabel>Expected Difficulty</InputLabel>
                    <Select label="Expected Difficulty" value={form.opponent_subjective_difficulty} onChange={(event) => updateForm({ opponent_subjective_difficulty: event.target.value })}>
                      {opponentSubjectiveDifficultyOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                ) : (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: form.opponent_edr_source === 'tennis_record' ? '0.8fr 1.4fr 0.8fr' : '0.8fr 1fr' }, gap: 1.5 }}>
                    <FormControl fullWidth>
                      <InputLabel>EDR Source</InputLabel>
                      <Select label="EDR Source" value={form.opponent_edr_source} onChange={(event) => updateForm({ opponent_edr_source: event.target.value as 'manual' | 'tennis_record' })}>
                        <MenuItem value="manual">Manual</MenuItem>
                        <MenuItem value="tennis_record">TennisRecord</MenuItem>
                      </Select>
                    </FormControl>
                    {form.opponent_edr_source === 'tennis_record' ? (
                      <>
                        <TextField label="TennisRecord Team URL" value={form.opponent_tennis_record_roster_url} onChange={(event) => updateForm({ opponent_tennis_record_roster_url: event.target.value })} fullWidth />
                        <Stack direction="row" spacing={1} alignItems="center">
                          <TextField label="Current Average EDR" value={form.opponent_edr_overall} InputProps={{ readOnly: true }} fullWidth />
                          <Tooltip title="Refresh Opponent EDR">
                            <span>
                              <Button variant="outlined" startIcon={<RefreshIcon />} disabled={opponentRefreshLoading || !form.opponent_tennis_record_roster_url.trim()} onClick={() => void refreshOpponentTennisRecord()}>
                                Refresh
                              </Button>
                            </span>
                          </Tooltip>
                        </Stack>
                      </>
                    ) : (
                      <TextField label="Opponent Average EDR" value={form.opponent_average_edr} onChange={(event) => updateForm({ opponent_average_edr: event.target.value })} fullWidth />
                    )}
                  </Box>
                )}
              </>
            ) : (
              <>
                <TextField label="Title" value={form.title} onChange={(event) => updateForm({ title: event.target.value })} required fullWidth />
                <TextField label="Description" value={form.description} onChange={(event) => updateForm({ description: event.target.value })} multiline minRows={3} fullWidth />
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select label="Status" value={form.status} onChange={(event) => updateForm({ status: event.target.value as TeamEventStatus })}>
                    {['Scheduled', 'Postponed', 'Rescheduled', 'Completed', 'Cancelled'].map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
                  </Select>
                </FormControl>
                <Box>
                  <FormControlLabel
                    control={<Switch checked={form.allow_maybe_rsvp} onChange={(event) => updateForm({ allow_maybe_rsvp: event.target.checked })} />}
                    label="Allow Maybe RSVPs"
                  />
                  <Typography variant="body2" color="text.secondary">Turn off to require players to respond Going or Not Going.</Typography>
                </Box>
                <TextField
                  label="RSVP Deadline"
                  type="datetime-local"
                  value={form.rsvp_deadline_local}
                  onChange={(event) => updateForm({ rsvp_deadline_local: event.target.value })}
                  InputLabelProps={{ shrink: true }}
                  helperText="Optional. Players can no longer change RSVPs or guests after this time."
                  error={Boolean(form.rsvp_deadline_local && form.start_time && (() => {
                    const deadline = deadlineInstantValue(form.rsvp_deadline_local, form.timezone);
                    const start = eventInstant(form.event_date, form.start_time, form.timezone)?.toString();
                    return deadline && start ? deadline >= start : false;
                  })())}
                  fullWidth
                />
              </>
            )}
          </Stack>
        </Paper>

        {!isMatch && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Audience</Typography>
            {form.audience_scope === 'workspace' ? (
              <Stack spacing={0.75}>
                <Typography variant="subtitle2">Workspace Event</Typography>
                <Typography variant="body2" color="text.secondary">All eligible players in {selectedWorkspaceName || 'this workspace'} can see and RSVP once, regardless of how many teams they belong to.</Typography>
                <Typography variant="caption" color="text.secondary">Workspace events use the Player Portal and are not published to GroupMe automatically.</Typography>
              </Stack>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>The primary team owns this event. It can also appear for other teams you manage.</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
                  <TextField label="Primary Team" value={selectedTeam?.team_name || ''} InputProps={{ readOnly: true }} fullWidth />
                  <FormControl fullWidth>
                    <InputLabel>Also Applies To</InputLabel>
                    <Select
                      multiple label="Also Applies To" value={form.additional_team_ids}
                      onChange={(event) => updateForm({ additional_team_ids: event.target.value as string[] })}
                      renderValue={(selected) => (selected as string[]).map((id) => teams.find((team) => team.id === id)?.team_name || id).join(', ')}
                    >
                      {teams.filter((team) => isEligibleAdditionalEventTeam(team, selectedTeam) || form.additional_team_ids.includes(team.id)).map((team) => (
                        <MenuItem key={team.id} value={team.id}><Checkbox checked={form.additional_team_ids.includes(team.id)} />{team.team_name}{!isEligibleAdditionalEventTeam(team, selectedTeam) ? ' (unavailable - remove)' : ''}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </>
            )}
          </Paper>
        )}

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Schedule & Location</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 1.5 }}>
            <TextField label={isMatch ? 'Match Date' : 'Date'} type="date" value={form.event_date} onChange={(event) => updateForm({ event_date: event.target.value })} InputLabelProps={{ shrink: true }} required fullWidth />
            {isMatch ? (
              <TextField label="Match Time" type="time" value={form.match_time} onChange={(event) => changeMatchTime(event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
            ) : (
              <TextField label="Start Time" type="time" value={form.start_time} onChange={(event) => changeEventStartTime(event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
            )}
            {isMatch && (
              <FormControl fullWidth>
                <InputLabel>Match Site Type</InputLabel>
                <Select
                  label="Match Site Type"
                  value={form.match_site_type}
                  onChange={(event) => {
                    const matchSiteType = event.target.value as MatchSiteType;
                    setForm((current) => applyFacilityChange(
                      { ...current, match_site_type: matchSiteType },
                      matchSiteType === 'Home' ? selectedTeam?.home_facility_id || '' : current.facility_id,
                    ));
                  }}
                >
                  {matchSiteTypeOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            {isMatch && (
              <Stack spacing={0.5}>
                <FormControlLabel control={<Checkbox checked={form.warmup_use_team_default} onChange={(event) => useTeamDefaultWarmup(event.target.checked)} />} label={`Use team default warmup (${defaultWarmupMinutesForTeam(form.team_id)} minutes before match)`} />
                <TextField label="Warmup Time" type="time" value={form.warmup_time} onChange={(event) => updateForm({ warmup_time: event.target.value })} InputLabelProps={{ shrink: true }} InputProps={{ readOnly: form.warmup_use_team_default }} helperText={form.warmup_use_team_default ? 'Calculated from match time and team setting' : 'Custom warmup time'} fullWidth />
              </Stack>
            )}
            <FormControl fullWidth>
              <InputLabel>Facility</InputLabel>
              <Select label="Facility" value={form.facility_id} onChange={(event) => setForm((current) => applyFacilityChange(current, event.target.value))}>
                <MenuItem value="">Location not set</MenuItem>
                {lookups.facilities.map((facility) => <MenuItem key={facility.value} value={facility.value}>{facility.label}</MenuItem>)}
              </Select>
            </FormControl>
            {!isMatch && (
              <TextField label="End Time" type="time" value={form.end_time} onChange={(event) => { updateForm({ end_time: event.target.value, groupme_event_end_time: event.target.value }); setGroupMeEndManuallyEdited(Boolean(event.target.value)); }} InputLabelProps={{ shrink: true }} fullWidth />
            )}
            <EventTimezoneField value={form.timezone} manual={form.timezone_manual_override} onChange={(timezone, manual) => updateForm({ timezone, timezone_manual_override: manual })} />
            {!isMatch && <TextField label="Location Text" value={form.location_text} onChange={(event) => updateForm({ location_text: event.target.value })} fullWidth />}
          </Box>
        </Paper>

        {(isMatch || form.audience_scope === 'selected_teams') && <>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>GroupMe Publishing</Typography>
              <Typography variant="body2" color="text.secondary">
                Configure the event fields CourtCaptain will send to GroupMe.
              </Typography>
            </Box>
            <FormControlLabel control={<Checkbox checked={form.groupme_sync_enabled} onChange={(event) => updateForm({ groupme_sync_enabled: event.target.checked })} />} label="Sync this event with GroupMe" />
          </Stack>
          {showGroupMeSection ? (
            <Stack spacing={1.5}>
              <TextField
                label="GroupMe Event Title"
                value={form.groupme_event_title}
                onChange={(event) => updateForm({ groupme_event_title: event.target.value })}
                helperText={isMatch ? 'Optional. Leave blank to generate the title from the opponent and match site.' : 'Defaults to the CourtCaptain event title when blank.'}
                fullWidth
              />
              <TextField label="GroupMe Event Description" value={form.groupme_event_description} onChange={(event) => updateForm({ groupme_event_description: event.target.value })} multiline minRows={3} fullWidth />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr auto auto' }, gap: 1.5, alignItems: 'center' }}>
                <TextField label="GroupMe Event Image URL" value={form.groupme_event_image_url} onChange={(event) => updateForm({ groupme_event_image_url: event.target.value })} fullWidth />
                <Button variant="outlined" startIcon={<UploadFileIcon />} component="label" disabled={groupMeImageUploading}>
                  Upload Image
                  <input hidden type="file" accept="image/*" onChange={(event) => void uploadGroupMeImage(event.target.files?.[0] || null)} />
                </Button>
                <Button variant="outlined" onClick={() => setImageLibraryOpen(true)} disabled={imageLibrary.length === 0}>Choose Existing</Button>
              </Box>
              {form.groupme_event_image_url && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box component="img" src={form.groupme_event_image_url} alt="GroupMe event" sx={{ width: 96, height: 64, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
                  <Button startIcon={<ContentCopyIcon />} onClick={() => void navigator.clipboard.writeText(form.groupme_event_image_url)}>Copy GroupMe image URL</Button>
                </Stack>
              )}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: isMatch ? '1fr 1fr' : '1fr' }, gap: 1.5 }}>
                {isMatch && (
                  <FormControl fullWidth>
                    <InputLabel>GroupMe Event Time</InputLabel>
                    <Select label="GroupMe Event Time" value={form.groupme_event_time_source} onChange={(event) => updateForm({ groupme_event_time_source: event.target.value as UnifiedEventForm['groupme_event_time_source'] })}>
                      <MenuItem value="warmup_time">{groupMeEventTimeOptionLabel('warmup_time')}</MenuItem>
                      <MenuItem value="match_time">{groupMeEventTimeOptionLabel('match_time')}</MenuItem>
                    </Select>
                  </FormControl>
                )}
                <TextField
                  label="GroupMe End Time"
                  type="time"
                  value={form.groupme_event_end_time || defaultGroupMeEndTimeForForm(form)}
                  onChange={(event) => {
                    setGroupMeEndManuallyEdited(true);
                    updateForm({ groupme_event_end_time: event.target.value });
                  }}
                  InputLabelProps={{ shrink: true }}
                  helperText={isMatch ? 'Defaults to two hours after the match time.' : 'Defaults to two hours after the event start time.'}
                  fullWidth
                />
              </Box>
              <TextField
                label="GroupMe Location Text"
                value={groupMeLocationTextPreview}
                onChange={(event) => updateForm({ groupme_location_text: event.target.value })}
                helperText={groupMeLocationTextHelperText}
                placeholder="No GroupMe location text will be sent"
                fullWidth
              />
              <TextField
                label="GroupMe Location Address"
                value={groupMeLocationAddressPreview}
                onChange={(event) => updateForm({ groupme_location_address: event.target.value })}
                helperText={groupMeLocationAddressHelperText}
                placeholder="No facility address will be sent"
                fullWidth
              />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: form.groupme_reminder_mode === 'custom' ? 'minmax(180px, 0.8fr) repeat(2, minmax(180px, 1fr))' : 'minmax(180px, 0.8fr) minmax(220px, 1fr)' }, gap: 1.5, alignItems: 'center' }}>
                <FormControl fullWidth>
                  <InputLabel>GroupMe Reminders</InputLabel>
                  <Select label="GroupMe Reminders" value={form.groupme_reminder_mode} onChange={(event) => updateForm({ groupme_reminder_mode: event.target.value as UnifiedEventForm['groupme_reminder_mode'] })}>
                    <MenuItem value="team_default">Use Team Defaults</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                </FormControl>
                {form.groupme_reminder_mode === 'custom' ? (
                  <>
                    {[0, 1].map((index) => (
                      <FormControl key={index} fullWidth>
                        <InputLabel>{index === 0 ? 'First Reminder' : 'Second Reminder'}</InputLabel>
                        <Select label={index === 0 ? 'First Reminder' : 'Second Reminder'} value={String(normalizeGroupMeReminders(form.groupme_custom_reminders)[index] ?? '')} onChange={(event) => updateGroupMeReminder(index, event.target.value)}>
                          <MenuItem value="">No reminder</MenuItem>
                          {groupMeReminderOptions.map((option) => <MenuItem key={option.value} value={String(option.value)}>{option.label}</MenuItem>)}
                        </Select>
                      </FormControl>
                    ))}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Team default: {groupMeReminderSummary(selectedTeam?.default_groupme_event_reminders)}
                  </Typography>
                )}
              </Box>
            </Stack>
          ) : (
            <Alert severity="info">Select a GroupMe-enabled team or turn on sync to configure GroupMe event details.</Alert>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>GroupMe Conversation Reminders</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Posts normal messages through each selected team’s configured bot. This does not depend on GroupMe Event Sync or a GroupMe Event ID.</Typography>
          <FormControlLabel control={<Checkbox checked={conversationRemindersEnabled} onChange={(event) => setConversationRemindersEnabled(event.target.checked)} />} label="Send reminders" />
          {conversationRemindersEnabled && <Stack spacing={1.5} sx={{ mt: 1 }}>
            {conversationReminders.map((reminder, index) => (
              <Stack key={index} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                <FormControlLabel control={<Checkbox checked={reminder.enabled} onChange={(event) => setConversationReminders((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, enabled: event.target.checked } : item))} />} label={`Reminder ${index + 1}`} />
                <TextField label="Amount" type="number" value={reminder.amount} inputProps={{ min: 1 }} onChange={(event) => setConversationReminders((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, amount: Math.max(1, Number(event.target.value) || 1) } : item))} sx={{ width: { xs: '100%', sm: 120 } }} />
                <FormControl sx={{ minWidth: { xs: '100%', sm: 150 } }}><InputLabel>Unit</InputLabel><Select label="Unit" value={reminder.unit} onChange={(event) => setConversationReminders((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, unit: event.target.value as ConversationReminder['unit'] } : item))}><MenuItem value="minutes">Minutes</MenuItem><MenuItem value="hours">Hours</MenuItem><MenuItem value="days">Days</MenuItem></Select></FormControl>
                <Typography variant="body2" color="text.secondary">before</Typography>
              </Stack>
            ))}
            <Box><Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Send to Groups For</Typography>{teams.filter((team) => [form.team_id, ...form.additional_team_ids].includes(team.id)).map((team) => <FormControlLabel key={team.id} control={<Checkbox checked={reminderAudienceTeamIds.includes(team.id)} onChange={(event) => setReminderAudienceTeamIds((current) => event.target.checked ? [...new Set([...current, team.id])] : current.filter((teamId) => teamId !== team.id))} />} label={team.team_name} />)}</Box>
          </Stack>}
        </Paper>
        </>}

        {isMatch && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Opposing Captain Contacts</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, columnGap: 1.5, rowGap: 2.5 }}>
              <TextField label="Opposing Captain Name" value={form.opponent_captain_names} onChange={(event) => updateForm({ opponent_captain_names: event.target.value })} fullWidth />
              <TextField label="Opposing Captain Email" value={form.opponent_captain_email} onChange={(event) => updateForm({ opponent_captain_email: event.target.value })} fullWidth />
              <TextField label="Opposing Captain Phone" value={form.opponent_captain_phone} onChange={(event) => updateForm({ opponent_captain_phone: event.target.value })} fullWidth />
              <Divider sx={{ gridColumn: '1 / -1', my: 1 }} />
              <TextField label="Opposing Co-Captain Name" value={form.opponent_co_captain_name} onChange={(event) => updateForm({ opponent_co_captain_name: event.target.value })} fullWidth />
              <TextField label="Opposing Co-Captain Email" value={form.opponent_co_captain_email} onChange={(event) => updateForm({ opponent_co_captain_email: event.target.value })} fullWidth />
              <TextField label="Opposing Co-Captain Phone" value={form.opponent_co_captain_phone} onChange={(event) => updateForm({ opponent_co_captain_phone: event.target.value })} fullWidth />
            </Box>
          </Paper>
        )}

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Internal Notes</Typography>
          <TextField label="Internal Notes" value={form.notes} onChange={(event) => updateForm({ notes: event.target.value })} multiline minRows={3} fullWidth />
        </Paper>
      </Stack>

      <Paper
        elevation={4}
        sx={{
          position: 'sticky',
          bottom: 0,
          mt: 2,
          p: 1.5,
          zIndex: 2,
          display: 'flex',
          justifyContent: 'space-between',
          gap: 1,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Button startIcon={<ArrowBackIcon />} onClick={cancelEdit} disabled={saving || deleting}>Cancel</Button>
          {mode === 'edit' && eventId && !readOnly && (
            <Button color="error" startIcon={<DeleteIcon />} onClick={() => void deleteCurrentEvent()} disabled={saving || deleting}>
              {deleting ? 'Deleting...' : isMatch ? 'Delete Match' : 'Delete Event'}
            </Button>
          )}
          <DraftSavedIndicator savedAt={draftPersistence.draftSavedAt} />
        </Stack>
        <Button variant="contained" onClick={() => void save()} disabled={saving || deleting || readOnly}>
          {saving ? 'Saving...' : saveLabel}
        </Button>
      </Paper>

      <DraftRecoveryDialog
        open={Boolean(draftPersistence.recoverableDraft)}
        updatedAt={draftPersistence.recoverableDraft?.updatedAt || null}
        onRestore={() => {
          const restored = draftPersistence.restoreDraft();
          if (restored) setForm(restored);
        }}
        onDiscard={draftPersistence.discardDraft}
      />
      <Dialog open={discardOpen} onClose={() => setDiscardOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Your changes have not been saved. Save changes to continue where you were going, or discard them.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setDiscardOpen(false)} disabled={saving}>Keep Editing</Button>
          <Button variant="outlined" onClick={() => void confirmSaveAndNavigate()} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button color="error" variant="contained" onClick={confirmDiscard} disabled={saving}>Discard Changes</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={imageLibraryOpen} onClose={() => setImageLibraryOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Choose Existing GroupMe Image</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            {imageLibrary.map((item) => (
              <Paper key={`${item.kind}:${item.id}`} variant="outlined" sx={{ p: 1.5, display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <Box component="img" src={item.imageUrl} alt="" sx={{ width: 96, height: 64, objectFit: 'cover', borderRadius: 1 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={700} noWrap>{item.title}</Typography>
                  <Typography variant="caption" color="text.secondary">{item.kind === 'match' ? 'Match' : 'Team Event'}</Typography>
                </Box>
                <Button onClick={() => { updateForm({ groupme_event_image_url: item.imageUrl }); setImageLibraryOpen(false); }}>Select</Button>
              </Paper>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageLibraryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={Boolean(snack)} autoHideDuration={4500} onClose={() => setSnack(null)}>
        {snack ? <Alert severity={snack.severity} onClose={() => setSnack(null)}>{snack.message}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}
